/**
 * @description Legacy `.rc-widget` label and USP customization for `<product-form>`.
 * Recharge injects `.rc-widget` asynchronously; a MutationObserver waits for it in the host subtree.
 */
class RechargeLegacyWidget extends HTMLElement {
  /** @type {RechargeLegacyConfig | null} */
  #legacyConfig = null;
  /** @type {HTMLElement | null} */
  #host = null;
  /** @type {MutationObserver | null} */
  #mountObserver = null;

  connectedCallback() {
    this.#host = this.closest('section').querySelector('product-form');
    if (!this.#host) return;
    this.#legacyConfig = this.#getLegacyConfig();
    this.#startObservers();
  }

  disconnectedCallback() {
    this.#mountObserver?.disconnect();
    this.#mountObserver = null;
  }

  #startObservers() {
    const target = this.#findTarget();
    if (target) {
      this.#applyLegacyWidgetConfig(target);
    } else {
      this.#watchMount();
    }
  }

  /** @returns {Element | null} */
  #findTarget() {
    return this.#host.querySelector('.rc-widget');
  }

  #watchMount() {
    this.#mountObserver?.disconnect();
    this.#mountObserver = new MutationObserver(() => {
      const target = this.#findTarget();
      if (!target) return;
      this.#mountObserver?.disconnect();
      this.#mountObserver = null;
      this.#applyLegacyWidgetConfig(target);
    });
    this.#mountObserver.observe(this.#host, { childList: true, subtree: true });
  }

  /** @returns {RechargeLegacyConfig | null} */
  #getLegacyConfig() {
    const el = this.querySelector('#recharge-legacy-subscription-widget');
    if (!el) return null;

    const raw = (el instanceof HTMLTemplateElement ? el.innerHTML : el.textContent)?.trim();
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * Apply legacy label + USP content only for Recharge legacy `.rc-widget`.
   * @param {Element} widgetOrHost
   */
  #applyLegacyWidgetConfig(widgetOrHost) {
    const widget = widgetOrHost.matches('.rc-widget')
      ? widgetOrHost
      : (widgetOrHost.querySelector?.('.rc-widget') ?? null);
    if (!widget) return;
    if (!this.#legacyConfig) return;

    this.#applyLegacySubscriptionLabel(widget);
    this.#applyLegacySubscriptionUsps(widget);
    this.#syncLegacyState(widget);

    if (widget.dataset.legacyWidgetEventsBound === 'true') return;

    // Recharge toggles purchase option via radio updates and label clicks.
    const sync = () => this.#syncLegacyState(widget);
    widget.addEventListener('change', sync);
    widget.addEventListener('click', sync);
    widget.dataset.legacyWidgetEventsBound = 'true';
  }

  /**
   * @param {Element} widget
   */
  #applyLegacySubscriptionLabel(widget) {
    const label = String(this.#legacyConfig?.label ?? '').trim();
    if (!label) return;

    const discountLabel = widget.querySelector('.subscription-radio .discount-label');
    if (!discountLabel) return;
    discountLabel.textContent = label;
  }

  /**
   * @param {Element} widget
   */
  #applyLegacySubscriptionUsps(widget) {
    const usps = [1, 2, 3]
      .map((idx) => this.#legacyConfig?.[`usp_${idx}`])
      .map((value) => String(value ?? '').trim())
      .filter(Boolean);

    if (!usps.length) return;

    const subscriptionRadio = widget.querySelector('.subscription-radio');
    if (!subscriptionRadio) return;

    let uspList = widget.querySelector('.sellingplan-usps');
    if (!uspList) {
      uspList = document.createElement('ul');
      uspList.className = 'sellingplan-usps';
    }

    let uspWrapper = widget.querySelector('.sellingplan-usp-wrapper');
    if (!uspWrapper) {
      uspWrapper = document.createElement('div');
      uspWrapper.className = 'sellingplan-usp-wrapper';
    }

    if (!subscriptionRadio.contains(uspWrapper)) {
      subscriptionRadio.appendChild(uspWrapper);
    }

    const sellingPlans = widget.querySelector('.rc-selling-plans');
    if (sellingPlans) {
      subscriptionRadio.insertBefore(sellingPlans, uspWrapper);
    }

    if (!uspWrapper.contains(uspList)) {
      uspWrapper.appendChild(uspList);
    }

    subscriptionRadio.classList.add('flex-wrap');
    uspList.classList.add('basis-full');

    uspList.replaceChildren(
      ...usps.map((usp) => {
        const li = document.createElement('li');
        li.textContent = usp;
        return li;
      })
    );
  }

  /**
   * Keep legacy USP list visible only for subscription selection.
   * @param {Element} widget
   */
  #syncLegacyState(widget) {
    const subscriptionInput = widget.querySelector('.subscription-radio input[type="radio"]');
    const oneTimeInput = widget.querySelector('.onetime-radio input[type="radio"]');
    const uspList = widget.querySelector('.sellingplan-usps');
    const uspWrapper = widget.querySelector('.sellingplan-usp-wrapper');
    if (!uspList || !uspWrapper) return;

    const subscriptionRadio = widget.querySelector('.subscription-radio');
    const isSubscriptionSelected = Boolean(
      subscriptionInput?.checked ||
      subscriptionRadio?.classList.contains('rc-radio--active') ||
      (oneTimeInput && !oneTimeInput.checked)
    );
    uspList.classList.toggle('hidden', !isSubscriptionSelected);
    uspWrapper.classList.toggle('hidden', !isSubscriptionSelected);
  }
}

customElements.define('recharge-legacy-widget', RechargeLegacyWidget);
